import type { HTMLAttributes } from "react";
import { altKey, modKey, shiftKey, useOS } from "@/shared/hooks/useOS";
import { cn } from "@/shared/lib/cn";

/**
 * Kbd — single key cap. Pass `keys` and we render each piece chained by `+`.
 * The special tokens `mod`, `alt`, `shift` are replaced with the
 * platform-correct glyph at render time.
 *
 *   <Kbd keys={["mod", "K"]} />        → ⌘ K  on macOS · Ctrl + K on Windows
 *   <Kbd keys={["mod", "shift", "P"]}/> → ⌘ ⇧ P  on macOS · Ctrl + Shift + P
 */

type Token = "mod" | "alt" | "shift" | (string & {});

export interface KbdProps extends HTMLAttributes<HTMLSpanElement> {
  keys: Token[];
}

export function Kbd({ keys, className, ...props }: KbdProps) {
  const os = useOS();
  const isMac = os === "mac" || os === "ios";

  const resolved = keys.map((k) => {
    if (k === "mod")   return modKey(os);
    if (k === "alt")   return altKey(os);
    if (k === "shift") return shiftKey(os);
    return k;
  });

  const sep = isMac ? "" : "+";

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 align-middle text-[var(--fg-subtle)]",
        className,
      )}
      aria-label={`Keyboard shortcut: ${resolved.join(" + ")}`}
      {...props}
    >
      {resolved.map((part, i) => (
        <span key={i} className="contents">
          {i > 0 && sep && (
            <span aria-hidden className="text-[10.5px] opacity-60">{sep}</span>
          )}
          <kbd
            className={cn(
              "inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5",
              "rounded-[5px] border border-[var(--border)] bg-[var(--bg-elevated)]",
              "text-[11px] font-medium tabular-nums tracking-tight text-[var(--fg-muted)]",
              "shadow-[inset_0_-1px_0_var(--separator)]",
              "select-none",
            )}
          >
            {part}
          </kbd>
        </span>
      ))}
    </span>
  );
}
